import { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid } from "recharts";
import { useMorph } from "../components/motion";
import { api } from "../lib/api";
import { useMarket } from "../lib/store";
import { useSubscribe } from "../lib/socket";
import { useBasket } from "../lib/basket";
import { useUnderlyings, useChain } from "../lib/useChain";
import { analyse } from "../lib/options";
import { fmt, signed } from "../lib/format";
import { Panel, PageHeader, Metric } from "../components/Panel";

const sel = "h-8 px-2 rounded-md border border-input bg-card text-[12px] outline-none focus:border-primary";
// [name, legs as [type, side, strikeOffsetSteps]]
const TEMPLATES = {
  "Long straddle": [["CE", "BUY", 0], ["PE", "BUY", 0]],
  "Short straddle": [["CE", "SELL", 0], ["PE", "SELL", 0]],
  "Long strangle": [["CE", "BUY", 2], ["PE", "BUY", -2]],
  "Short strangle": [["CE", "SELL", 2], ["PE", "SELL", -2]],
  "Bull call spread": [["CE", "BUY", 0], ["CE", "SELL", 2]],
  "Bear put spread": [["PE", "BUY", 0], ["PE", "SELL", -2]],
  "Iron condor": [["PE", "BUY", -4], ["PE", "SELL", -2], ["CE", "SELL", 2], ["CE", "BUY", 4]],
  "Iron fly": [["PE", "BUY", -3], ["PE", "SELL", 0], ["CE", "SELL", 0], ["CE", "BUY", 3]],
  "Covered call": [["FUT", "BUY", 0], ["CE", "SELL", 2]],
};

export default function Strategy() {
  const unds = useUnderlyings();
  const [und, setUnd] = useState("NSE_INDEX|Nifty 50");
  const [tpl, setTpl] = useState("Iron condor");
  const [lots, setLots] = useState(1);
  const { expiries, expiry, setExpiry, rows, chain, spot, lot } = useChain(und, 30);
  const ticks = useMarket((s) => s.ticks);
  const meta = useMarket((s) => s.meta);
  const basket = useBasket();
  const all = chain?.strikes || [];
  const atmIdx = all.findIndex((r) => r.strike === rows[Math.floor(rows.length / 2)]?.strike);
  const [futs, setFuts] = useState([]);
  useEffect(() => { api.get("/fno/futures").then((r) => setFuts(r.data)).catch(() => {}); }, []);
  const fut = futs.find((f) => f.underlying_key === und);
  const futKey = fut?.instrument_key;
  useEffect(() => { if (fut) basket && useMarket.getState().addMeta({ [fut.instrument_key]: { ...fut, instrument_type: "FUT" } }); }, [fut]); // eslint-disable-line react-hooks/exhaustive-deps
  const legs = (TEMPLATES[tpl] || []).map(([type, side, off]) => {
    if (type === "FUT") return { type, side, key: futKey || und, strike: 0 };
    const r = all[Math.min(Math.max(atmIdx + off, 0), all.length - 1)];
    return { type, side, key: r?.[type], strike: r?.strike };
  }).filter((l) => l.key);
  useSubscribe([und, ...legs.map((l) => l.key)]);
  const priced = legs.map((l) => {
    const t = ticks[l.key];
    const iv = t?.iv ? (t.iv > 1 ? t.iv / 100 : t.iv) : 0.15;
    return { ...l, entry: l.type === "FUT" ? t?.ltp ?? spot : t?.ltp ?? 0, iv, expiry, qty: lots * lot };
  });
  const ready = spot && priced.length && priced.every((l) => l.entry);
  const sig = ready ? priced.map((l) => `${l.key}:${l.side}:${l.qty}:${l.entry}`).join("|") + spot : "";
  const res = useMemo(() => (ready ? analyse(priced, spot) : null), [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  const morphed = useMorph(res?.pts, ["expiry", "today"]);
  const chartPts = useMemo(() => (morphed || []).map((p) => ({ ...p, gain: Math.max(0, p.expiry), loss: Math.min(0, p.expiry) })), [morphed]);
  const yDom = useMemo(() => {
    if (!res?.pts) return ["auto", "auto"];
    const ys = res.pts.flatMap((p) => [p.expiry, p.today]);
    const lo = Math.min(0, ...ys), hi = Math.max(0, ...ys), pad = (hi - lo) * 0.08 || 1;
    return [Math.floor(lo - pad), Math.ceil(hi + pad)];
  }, [res]);
  const execute = () => {
    basket.setLegs(priced.map((l) => ({ id: Math.random().toString(36).slice(2), key: l.key, side: l.side, lots, product: "NRML", order_type: "MARKET", price: 0 })));
    basket.setOpen(true);
  };
  const fmtCap = (v) => (v === Infinity ? "Unlimited" : v === -Infinity ? "Unlimited" : signed(v));
  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="strategy-page">
      <PageHeader title="Strategy builder">
        <select data-testid="strategy-underlying" value={und} onChange={(e) => setUnd(e.target.value)} className={sel}>{unds.map((u) => <option key={u.underlying_key} value={u.underlying_key}>{u.symbol}</option>)}</select>
        <select data-testid="strategy-expiry" value={expiry} onChange={(e) => setExpiry(e.target.value)} className={sel}>{expiries.map((x) => <option key={x}>{x}</option>)}</select>
        <select data-testid="strategy-template" value={tpl} onChange={(e) => setTpl(e.target.value)} className={sel}>{Object.keys(TEMPLATES).map((k) => <option key={k}>{k}</option>)}</select>
        <input data-testid="strategy-lots" type="number" min="1" value={lots} onChange={(e) => setLots(Math.max(1, Number(e.target.value) || 1))} className={`${sel} w-16 num`} />
        <button data-testid="strategy-execute" disabled={!ready} onClick={execute} className="h-8 px-4 rounded-md bg-primary text-primary-foreground font-semibold text-[12px] disabled:opacity-50">Execute as basket</button>
      </PageHeader>
      <Panel>
        <div className="grid grid-cols-2 lg:grid-cols-5 divide-x divide-border">
          <Metric label="Max profit" value={<span className="text-up">{res ? fmtCap(res.maxProfit) : "—"}</span>} testid="strategy-max-profit" />
          <Metric label="Max loss" value={<span className="text-down">{res ? fmtCap(res.maxLoss) : "—"}</span>} testid="strategy-max-loss" />
          <Metric label="Breakevens" value={res?.breakevens.length ? res.breakevens.map(fmt).join(" · ") : "—"} testid="strategy-breakevens" />
          <Metric label="Prob. of profit" value={res ? `${(res.pop * 100).toFixed(0)}%` : "—"} sub="approx., lognormal" testid="strategy-pop" />
          <Metric label="Spot" value={fmt(spot)} sub={`Lot ${lot}`} />
        </div>
      </Panel>
      <div className="grid xl:grid-cols-[1fr_360px] gap-4">
        <Panel title="Payoff · at expiry (solid) and today (dashed)">
          <div className="h-[340px] p-2" data-testid="strategy-payoff-chart">
            {res && chartPts.length > 0 && (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartPts}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis dataKey="S" type="number" domain={["dataMin", "dataMax"]} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis domain={yDom} allowDataOverflow tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" width={70} />
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 11 }} formatter={(v) => fmt(v)} labelFormatter={(l) => `Spot ${l}`} />
                  <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" />
                  <ReferenceLine x={Math.round(spot)} stroke="#D4AF37" strokeDasharray="4 4" />
                  <Area dataKey="gain" baseValue={0} stroke="none" fill="hsl(var(--up))" fillOpacity={0.18} isAnimationActive={false} />
                  <Area dataKey="loss" baseValue={0} stroke="none" fill="hsl(var(--down))" fillOpacity={0.18} isAnimationActive={false} />
                  <Line dataKey="expiry" stroke="#1FBF8F" dot={false} strokeWidth={2} isAnimationActive={false} />
                  <Line dataKey="today" stroke="#E8C96A" dot={false} strokeDasharray="5 4" isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>)}
          </div>
        </Panel>
        <Panel title="Legs">
          <div className="divide-y divide-border/60">
            {priced.map((l, i) => (
              <div key={i} className="flex items-center justify-between px-4 h-10 text-[12px]" data-testid={`strategy-leg-${i}`}>
                <span><span className={`text-[10px] font-bold mr-2 ${l.side === "BUY" ? "text-up" : "text-down"}`}>{l.side}</span>{meta[l.key]?.trading_symbol || l.type}</span>
                <span className="num">{fmt(l.entry)}</span>
              </div>))}
            <div className="px-4 py-3 text-[11px] text-muted-foreground">Margin is shown in the basket before placing (sum of legs; hedge benefit not applied yet).</div>
          </div>
        </Panel>
      </div>
    </div>
  );
}
